import BigNumber from "bignumber.js";
import env from "../env.json";
import React from "react";
import { usePayload } from "./token";
import { getConfig } from "../api/core";
import { getMappedCode, getParsingRules, type CodeFormatRow } from "../api/inventory";

type InventoryRule = {
    sku: Array<string> | string;
    divide?: Array<number>;
    match?: boolean;
    display?: string | Array<string>;
};

type ParseRules = {
    fields: Array<string>;
    sku: Array<string>;
    rules: Array<CodeFormatRow>;
};

const TagContext = React.createContext<ParseRules>(env.defaultParseRules);

export function TagProvider(props: { children: React.ReactNode }) {
    let { 0: rules, 1: setRules } = React.useState<ParseRules>(env.defaultParseRules);
    let { 0: payload } = usePayload();
    const onLoad = React.useCallback((params: { config: Record<string, string>, rules: Array<CodeFormatRow> } | null) => {
        if (!params) {
            return;
        }
        const { config, rules } = params;
        try {
            ScannedTag.rules.combine = JSON.parse(config['inventory/combine'] ?? '[]') ?? [];
        } catch { }
        try {
            ScannedTag.rules.confirm = JSON.parse(config['inventory/confirm'] ?? '[]') ?? [];
        } catch { }
        try {
            ScannedTag.rules.pad = JSON.parse(config['inventory/pad'] ?? '[]') ?? [];
        } catch { }
        try {
            ScannedTag.rules.separate = JSON.parse(config['inventory/separate'] ?? '[]') ?? [];
        } catch { }
        try {
            ScannedTag.rules.skip = JSON.parse(config['inventory/skip'] ?? '[]') ?? [];
        } catch { }
        try {
            let parsed: any = null;
            parsed = JSON.parse(config['inventory/code_format'] ?? 'null');
            if (parsed) {
                if (typeof parsed.fields === 'string') {
                    parsed.fields = parsed.fields.trim().split(/\s*[,\uff0c]\s*/);
                }
                if (typeof parsed.sku === 'string') {
                    parsed.sku = parsed.sku.trim().split(/\s*[,\uff0c]\s*/);
                }
                parsed.rules = rules;
                setRules(parsed);
                TagInfo.parseRules = parsed;
            }
        } catch {
        }
    }, [setRules]);
    React.useEffect(() => {
        if (payload?.package) {
            Promise.all([getConfig(['inventory/combine', 'inventory/confirm', 'inventory/pad', 'inventory/code_format', 'inventory/skip', 'inventory/separate']), getParsingRules()]).then(results => {
                try {
                    let config = { config: results[0] as Record<string, string>, rules: results[1] as Array<CodeFormatRow> };
                    onLoad(config);
                } catch { }
            });
        }
    }, [payload, setRules, onLoad]);
    return (
        <TagContext.Provider value={rules}>{props.children}</TagContext.Provider>
    );
}

export function useTagParsing() {
    return React.useContext(TagContext);
}

export default class TagInfo {
    static parseRules: ParseRules = env.defaultParseRules;
    static cache: Record<string, string> = {};

    public hex = '';
    private debounce = false;
    private _id = '';
    public get id() {
        return this._id;
    }
    public set id(value: string) {
        this._id = value.replace(/^\#0+/, '#');
        if (this.code) {
            typeof this.callback === 'function' && this.callback(this);
            return;
        }
        if (!this.debounce) {
            this.debounce = true;
            let readCache = (cached: { code: string, parts: Record<string, string>, rule: number } | string | null) => {
                if (cached) {
                    this.debounce = false;
                    if (typeof cached === 'string') {
                        this.code = cached;
                        let tag = TagInfo.fromEpc(this.code);
                        this.parts = tag.parts;
                        this.rule = tag.rule;
                    } else {
                        this.code = cached.code;
                        this.parts = cached.parts;
                        this.rule = cached.rule;
                    }
                    typeof this.callback === 'function' && this.callback(this);
                } else {
                    getMappedCode(this._id).then(response => {
                        if (typeof response !== 'string') {
                            this.debounce = false;
                            this.code = response[this._id];
                            let tag = TagInfo.fromEpc(this.code);
                            this.parts = tag.parts;
                            this.rule = tag.rule;
                            localStorage.setItem('code-' + this._id, JSON.stringify({ code: this.code, parts: this.parts, rule: this.rule }));
                            this.version++;
                            typeof this.callback === 'function' && this.callback(this);
                        }
                    });
                }
            };
            if (TagInfo.cache[this._id]) {
                readCache(TagInfo.cache[this._id]);
            } else {
                let cached = localStorage.getItem('code-' + this._id);
                try {
                    readCache(cached ? JSON.parse(cached) : null);
                } catch {
                    readCache(cached);
                }
            }
        }
    }

    private _code = '';
    public get code() {
        return this._code;
    }
    public set code(value: string) {
        if (value.startsWith('#')) {
            this.id = value;
        } else {
            let p = value.split('@');
            this._code = p[0];
            if (p[1]) {
                this.rule = parseInt(p[1]);
            }
        }
    }
    private _rule: CodeFormatRow | null = null;
    public get rule(): CodeFormatRow | null {
        return this._rule;
    }
    public set rule(value: CodeFormatRow | null | number) {
        if (typeof value !== 'object') {
            for (let rule of TagInfo.parseRules.rules) {
                if (rule.id?.toString() === value.toString()) {
                    value = rule;
                    break;
                }
            }
        }
        this._rule = typeof value !== 'object' ? null : value;
    }

    public parts: Record<string, string> = {};
    public rssi = -100;
    public get sku() {
        if (!this.rule) {
            return this.code;
        }
        let tmp = [];
        for (let key of (this.rule.config?.sku || TagInfo.parseRules.sku)) {
            typeof this.parts[key] !== 'undefined' && tmp.push(this.parts[key]);
        }
        return tmp.join(this.rule.separator || '-');
    }
    public version = 0;
    public callback?: (tag: TagInfo) => void;

    static getRule(id: string) {
        return new Promise<CodeFormatRow | null>(resolve => {
            if (id === '-1') {
                resolve(null);
                return;
            }
            for (let rule of TagInfo.parseRules.rules) {
                if (rule.id?.toString() === id) {
                    resolve(rule);
                    return;
                }
            }
            getParsingRules(parseInt(id)).then(rules => {
                if (typeof rules === 'string' || rules.length === 0) {
                    resolve(null);
                } else {
                    TagInfo.parseRules.rules.push(rules[0]);
                    resolve(rules[0]);
                }
            }, () => resolve(null));
        });
    }

    toMap() {
        return {
            hex: this.hex,
            id: this.id,
            code: this.code,
            parts: this.parts,
            rule: this.rule,
            rssi: this.rssi
        };
    }

    toJSON() {
        return JSON.stringify(this.toMap());
    }

    clone() {
        return TagInfo.fromMap(this.toMap());
    }

    static fromEpc(code: string, rule: number, callback?: (tag: TagInfo) => void): Promise<TagInfo>;
    static fromEpc(code: string, rule: CodeFormatRow | null, callback?: () => void): TagInfo;
    static fromEpc(code: string, callback?: (tag: TagInfo) => void): TagInfo;
    static fromEpc(code: string, rule: number | CodeFormatRow | null | ((tag: TagInfo) => void) | undefined, callback?: (tag: TagInfo) => void): TagInfo | Promise<TagInfo> {
        let result = new TagInfo();
        let after = () => {
            if (code.startsWith('#')) {
                result.hex = TagInfo.code2hex(code);
                result.id = code;
                return result;
            } else if (code.includes('-') || /[^0-9a-fA-F]/.test(code)) {
                result.hex = TagInfo.code2hex(code);
                result.code = code;
            } else {
                result.hex = code;
                result.code = TagInfo.hex2code(code);
            }
            if (result.code.includes('-*-*-')) {
                result.code = result.code.substring(0, result.code.indexOf('-*-*-'));
                result.rule = null;
                result.callback?.(result);
                return result;
            }
            result.code = TagInfo.pad(result.code);
            let tag = TagInfo.fromParts(result);
            result.callback?.(tag);
            return tag;
        };
        if (typeof rule === 'number') {
            result.callback = callback;
            return new Promise(resolve => {
                TagInfo.getRule(rule.toString()).then(rule => {
                    result.rule = rule;
                    resolve(after());
                });
            });
        } else if (typeof rule === 'object') {
            result.rule = rule;
            result.callback = callback;
            return after();
        } else {
            result.callback = rule;
            return after();
        }
    }

    private static pad(code: string): string {
        for (let rule of ScannedTag.rules.pad ?? []) {
            if (code.startsWith(rule.sku as string)) {
                let cut = typeof rule.divide === 'object' ? rule.divide[0] : (rule.divide || 0);
                return (rule.display || '') + code.substring(rule.sku.length, code.length - cut) + (cut > 0 ? ("-" + code.substring(code.length - (rule.divide?.[0] || 0))) : '');
            }
        }
        return code;
    }

    private static fromParts(result: TagInfo): TagInfo {
        let parsed: TagInfo | null = null;
        if (result.rule) {
            parsed = TagInfo.fromPartsWithRule(result, result.rule);
        } else {
            for (let rule of TagInfo.parseRules.rules) {
                parsed = TagInfo.fromPartsWithRule(result, rule);
                if (parsed) {
                    break;
                }
            }
        }
        return parsed ?? result;
    }

    private static fromPartsWithRule(result: TagInfo, rule: CodeFormatRow): TagInfo | null {
        let parts = result.code.split(rule.separator || '-');
        if (parts.length < rule.min || rule.max && parts.length > rule.max) {
            return null;
        }
        let star = '';
        let fields = rule.config?.fields ?? this.parseRules.fields;
        for (let i = 0; i < fields.length; i++) {
            if (rule.fields[i] == '*') {
                star = fields[i];
                break;
            } else if (rule.fields[i]) {
                let part = [], count = parseInt(rule.fields[i]);
                for (let j = 0; j < count; j++) {
                    part.push(parts.shift());
                }
                result.parts[fields[i]] = part.join(rule.separator);
            }
        }
        if (star && parts.length > 0) {
            for (let i = fields.length - 1; i > 0; i--) {
                if (rule.fields[i] == '*') {
                    star = fields[i];
                    break;
                } else {
                    let part = [], count = parseInt(rule.fields[i]);
                    for (let j = 0; j < count; j++) {
                        part.unshift(parts.pop());
                    }
                    result.parts[fields[i]] = part.join(rule.separator);
                }
            }
        }
        if (star.length > 0) {
            result.parts[star] = parts.join(rule.separator);
        }
        result.rule = rule;
        return result
    }

    static fromMap(map: Record<string, any>, callback?: (tag: TagInfo) => void): TagInfo {
        let result = new TagInfo();
        result.callback = callback;
        result.hex = map.hex ?? '';
        result.parts = map.parts ?? {};
        result.rule = map.rule ?? -1;
        result.rssi = map.rssi ?? -100;
        result.code = map.code ?? '';
        result.id = map.id ?? '';
        if (!map.id) {
            callback?.(result);
        }
        return result;
    }

    static hex2code(code: string): string {
        let result = '';
        for (let i = 0; i < code.length; i += 2) {
            let char = String.fromCharCode(parseInt(code.substring(i, i + 2), 16));
            if (char === '\0') {
                break;
            }
            result += char;
        }
        return result;
    }

    static code2hex(code: string, align = true): string {
        let result = '';
        for (let i = 0; i < code.length; i++) {
            result += code.charCodeAt(i).toString(16);
        }
        return result.toUpperCase() + (align && result.length % 4 ? '00' : '');
    }
}

export class ScannedTag {
    static rules = {
        combine: [] as InventoryRule[],
        confirm: [] as InventoryRule[],
        pad: [] as InventoryRule[],
        separate: [] as InventoryRule[],
        skip: [] as InventoryRule[]
    };

    public id = '';
    private _sku = '';
    private _skip = false;
    public get sku() {
        return this._sku;
    }
    public set sku(value: string) {
        this._sku = value;
        this._skip = ScannedTag.isSkipped(value);
        if (this._skip) {
            setTimeout(this.fill.bind(this), 10);
        }
    }
    public qty = 0;
    public get status(): 'less' | 'more' | 'match' | 'skip' | 'warn' {
        if (this._skip) {
            return 'skip';
        } else if (this.tags.size === this.qty) {
            return 'match';
        } else if (this.tags.size > this.qty) {
            return this.qty === 0 ? 'warn' : 'more';
        } else {
            return 'less';
        }
    }
    public tags = new Set<string>();
    public get scannedQty() {
        return (new BigNumber(this.tags.size)).div(this.divide).toNumber();
    }
    protected _rule: InventoryRule | null | undefined;
    public get rule(): InventoryRule | null {
        if (typeof this._rule === 'undefined') {
            this._rule = null;
            for (let rule of ScannedTag.rules.combine) {
                if (ScannedTag.matchSku(this.sku, rule)) {
                    this._rule = rule;
                    break;
                }
            }
        }
        return this._rule;
    }
    public set rule(rule: InventoryRule | null) {
        this._rule = rule;
    }
    protected get divide() {
        return (this.rule?.divide ?? [1])[0];
    }

    fill() {
        let step = (new BigNumber(this.divide)).pow(-1);
        for (let i = new BigNumber(this.scannedQty); i.lt(this.qty); i = i.plus(step)) {
            this.tags.add(this.sku + '-*-*-' + i.toNumber());
        }
    }

    clone() {
        let cloned = new ScannedTag();
        cloned.rule = this.rule;
        cloned.tags = new Set(this.tags);
        cloned.id = this.id;
        cloned.sku = this.sku;
        cloned.qty = this.qty;
        return cloned;
    }

    static fromMap(map: Record<string, any>) {
        let item = new ScannedTag();
        item.id = map['id'] ?? '';
        item.sku = map['sku'] ?? '';
        item.qty = parseFloat((map['qty'] ?? '0').toString());
        return ScannedTag.separate(item);
    }

    static separate(tags: TagInfo): Array<TagInfo>;
    static separate(scanned: ScannedTag): Array<ScannedTag>;
    static separate(scanned: ScannedTag | TagInfo): Array<ScannedTag | TagInfo> {
        let result: Array<ScannedTag | TagInfo> = [];
        for (let rule of ScannedTag.rules.separate) {
            let match = ScannedTag.matchSku(scanned.sku, rule);
            if (match) {
                for (let i in rule.display as Array<string>) {
                    let item: ScannedTag | TagInfo;
                    if (scanned instanceof TagInfo) {
                        item = TagInfo.fromEpc(scanned.code.replace(match, (rule.display as Array<string>)[i]), scanned.rule);
                    } else {
                        item = new ScannedTag();
                        item.id = scanned['id'] ?? '';
                        item.sku = (scanned['sku'] ?? '').replace(match, (rule.display as Array<string>)[i]);
                        item.qty = scanned.qty * (rule.divide?.[i] ?? 1);
                    }
                    result.push(item);
                }
                break;
            }
        }
        return result.length ? result : [scanned];
    }

    private static matchSku(sku: string, rule: InventoryRule): RegExp | string | null {
        let result: RegExp | string | null = null;
        if (rule.match) {
            if (typeof rule.sku === 'string') {
                result = new RegExp(rule.sku);
                if (!result.test(sku)) {
                    result = null;
                }
            } else {
                for (let part of typeof rule.sku === 'string' ? [rule.sku] : rule.sku) {
                    if (sku === part) {
                        result = part;
                        break;
                    }
                }
            }
        } else {
            for (let part of typeof rule.sku === 'string' ? [rule.sku] : rule.sku) {
                if (sku.includes(part)) {
                    result = part;
                    break;
                }
            }
        }
        return result;
    }

    static matchTag(tag: TagInfo, scanned: Array<ScannedTag>): Array<ScannedTag> {
        let flag = true;
        for (let item of scanned) {
            if (item.rule === null ? tag.sku === item.sku : ScannedTag.matchSku(tag.sku, item.rule)) {
                item.tags.add(tag.code);
                flag = false;
                break;
            }
        }
        if (flag) {
            scanned.push(ScannedTag.fromTag(tag));
        }
        return scanned;
    }

    private static isSkipped(sku: string) {
        for (let rule of ScannedTag.rules.skip) {
            if (ScannedTag.matchSku(sku, rule)) {
                return true;
            }
        }
        return false;
    }

    static confirm(scanned: Array<ScannedTag>) {
        let result: Record<string, 1> = {};
        for (let item of scanned) {
            for (let rule of ScannedTag.rules.confirm) {
                let match = ScannedTag.matchSku(item.sku, rule);
                if (match) {
                    let msg = (rule.display as string)?.replace('%s', item.sku).replace('%0$d', item.qty.toString());
                    for (let i = 1; i <= (rule.divide?.length ?? 0); i++) {
                        msg = msg.replace('%' + i + '$d', (item.qty * rule.divide![i]).toString());
                    }
                    result[msg] = 1;
                    break;
                }
            }
        }
        return Object.keys(result);
    }

    static fromTag(tag: TagInfo) {
        let result = new ScannedTag();
        result.sku = tag.sku;
        result.tags.add(tag.code);
        return result;
    }
}
